import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { FaSave, FaUserPlus, FaLayerGroup, FaTrashAlt, FaUndo, FaTeeth, FaEdit, FaTrophy, FaRocket } from 'react-icons/fa';
import confetti from 'canvas-confetti';
import DentalArch from '../components/DentalArch';
import ConfigPanel from '../components/ConfigPanel';
import PractitionerForm from '../components/PractitionerForm';
import Toast from '../components/Toast';
import TeethSummary from '../components/TeethSummary';
import OrderScreenshotParser from '../components/OrderScreenshotParser';
import RocketCelebration from '../components/RocketCelebration';
import '../styles/NewJob.css';

const DRAFT_STORAGE_KEY = 'enovadent_newjob_draft';

const loadDraft = () => {
    try {
        const saved = sessionStorage.getItem(DRAFT_STORAGE_KEY);
        return saved ? JSON.parse(saved) : null;
    } catch (e) {
        console.error('Error loading draft from sessionStorage:', e);
        return null;
    }
};

function NewJob() {
    const navigate = useNavigate();
    const { orderId } = useParams();
    const { addOrder, updateOrder, orders, clients, products } = useData();

    // Restore draft from sessionStorage if creating a new job (no orderId in URL)
    const [initialDraft] = useState(() => (!orderId ? loadDraft() : null));

    const [selectedTeeth, setSelectedTeeth] = useState(() => initialDraft?.selectedTeeth || []);
    const [teethConfig, setTeethConfig] = useState(() => initialDraft?.teethConfig || []);
    const [editingLineId, setEditingLineId] = useState(null);
    // Use a ref for in-progress config to avoid re-rendering the parent on every keystroke
    const inProgressConfigRef = useRef(initialDraft?.inProgressConfig || null);
    const [initialInProgressConfig] = useState(() => initialDraft?.inProgressConfig || null);
    const [toast, setToast] = useState(null);
    const [showEno100Modal, setShowEno100Modal] = useState(false);
    const [showEno300Modal, setShowEno300Modal] = useState(false);
    const [rocketActive, setRocketActive] = useState(false);
    const [createdCaseNumber, setCreatedCaseNumber] = useState('');
    const [practitionerInfo, setPractitionerInfo] = useState(() => initialDraft?.practitionerInfo || {
        name: '', address: '', phone: '', email: ''
    });
    const [patientInfo, setPatientInfo] = useState(() => initialDraft?.patientInfo || {
        reference: '', name: '', gender: '', age: ''
    });
    const [appointmentDate, setAppointmentDate] = useState(() => initialDraft?.appointmentDate || '');
    const [gamme, setGamme] = useState(() => initialDraft?.gamme || 'nova');
    const [notes, setNotes] = useState(() => initialDraft?.notes || '');

    // Stable callback for ConfigPanel draft sync (no re-render)
    const handleDraftChange = useCallback((draft) => {
        inProgressConfigRef.current = draft;
    }, []);

    const playCelebrationFanfare = () => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            const ctx = new AudioCtx();

            const notes = [
                { freq: 523.25, time: 0, dur: 0.15 },
                { freq: 659.25, time: 0.15, dur: 0.15 },
                { freq: 783.99, time: 0.30, dur: 0.15 },
                { freq: 1046.50, time: 0.45, dur: 0.45 },
                { freq: 880.00, time: 0.95, dur: 0.15 },
                { freq: 1046.50, time: 1.10, dur: 0.70 }
            ];

            notes.forEach(n => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.value = n.freq;

                gain.gain.setValueAtTime(0, ctx.currentTime + n.time);
                gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + n.time + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + n.time + n.dur);

                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(ctx.currentTime + n.time);
                osc.stop(ctx.currentTime + n.time + n.dur);
            });
        } catch (e) {
            console.warn('AudioContext unavailable:', e);
        }
    };

    const triggerCelebrationConfetti = () => {
        playCelebrationFanfare();

        // Wave 1: Center explosive burst
        confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.55 },
            colors: ['#ffd700', '#ff3e3e', '#00e5ff', '#10b981', '#a855f7', '#ff69b4'],
            scalar: 1.2
        });

        // Wave 2: Left cannon
        setTimeout(() => {
            confetti({
                particleCount: 90,
                angle: 60,
                spread: 75,
                origin: { x: 0, y: 0.65 },
                colors: ['#ffd700', '#ff4500', '#00e5ff', '#76ff03']
            });
        }, 300);

        // Wave 3: Right cannon
        setTimeout(() => {
            confetti({
                particleCount: 90,
                angle: 120,
                spread: 75,
                origin: { x: 1, y: 0.65 },
                colors: ['#ffd700', '#ff007f', '#7928ca', '#00dfd8']
            });
        }, 600);

        // Wave 4: Sky fireworks shower
        setTimeout(() => {
            confetti({
                particleCount: 180,
                spread: 160,
                origin: { y: 0.25 },
                ticks: 350,
                gravity: 0.7,
                scalar: 1.3
            });
        }, 1000);
    };

    const triggerRocketCelebration = () => {
        setRocketActive(false);
        setTimeout(() => setRocketActive(true), 30);
    };

    const normalizeTeethConfig = (config) => {
        if (Array.isArray(config)) return config;
        if (config && typeof config === 'object') {
            return Object.entries(config).map(([toothId, details]) => {
                const product = products.find(p => p.id === details.productId);
                return {
                    id: `legacy-${toothId}-${Date.now()}`,
                    teeth: [toothId],
                    ...details,
                    name: product ? product.name : 'Legacy Item'
                };
            });
        }
        return [];
    };

    useEffect(() => {
        if (orderId && orders.length > 0) {
            const orderToEdit = orders.find(o => o.id === orderId);
            if (orderToEdit) {
                setPractitionerInfo({
                    id: orderToEdit.clientId,
                    name: orderToEdit.practitionerName || '',
                    address: '', phone: '', email: ''
                });
                setPatientInfo({
                    reference: orderToEdit.patientReference || '',
                    name: orderToEdit.patientName || '',
                    gender: '', age: ''
                });
                setTeethConfig(normalizeTeethConfig(orderToEdit.teethConfig));
                setGamme(orderToEdit.gamme || 'nova');
                setAppointmentDate(orderToEdit.appointmentDate || '');
                setNotes(orderToEdit.notes || '');
            }
        }
    }, [orderId, orders]);

    // Pre-fill practitioner if arriving from client dossier (?clientId=...)
    useEffect(() => {
        if (!orderId) {
            const searchParams = new URLSearchParams(window.location.search);
            const queryClientId = searchParams.get('clientId');
            if (queryClientId && clients && clients.length > 0) {
                const found = clients.find(c => c.id === queryClientId);
                if (found) {
                    setPractitionerInfo({
                        id: found.id,
                        name: found.name || '',
                        address: found.address || '',
                        phone: found.phone || '',
                        email: found.email || ''
                    });
                }
            }
        }
    }, [orderId, clients]);

    // Automatically persist draft to sessionStorage for new cases
    useEffect(() => {
        if (!orderId) {
            try {
                const currentDraftConfig = inProgressConfigRef.current;
                const hasData = Boolean(
                    practitionerInfo.name || practitionerInfo.address || practitionerInfo.phone ||
                    patientInfo.name || patientInfo.reference ||
                    (selectedTeeth && selectedTeeth.length > 0) ||
                    (teethConfig && teethConfig.length > 0) ||
                    notes || appointmentDate ||
                    (currentDraftConfig && (currentDraftConfig.notes || currentDraftConfig.category || currentDraftConfig.productId))
                );

                if (hasData) {
                    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
                        practitionerInfo,
                        patientInfo,
                        selectedTeeth,
                        teethConfig,
                        appointmentDate,
                        gamme,
                        notes,
                        inProgressConfig: currentDraftConfig
                    }));
                } else {
                    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
                }
            } catch (e) {
                console.error('Error saving draft:', e);
            }
        }
    }, [orderId, practitionerInfo, patientInfo, selectedTeeth, teethConfig, appointmentDate, gamme, notes]);

    const handleResetDraft = () => {
        if (window.confirm("Voulez-vous effacer le brouillon et réinitialiser tous les champs de la fiche ?")) {
            sessionStorage.removeItem(DRAFT_STORAGE_KEY);
            setPractitionerInfo({ name: '', address: '', phone: '', email: '' });
            setPatientInfo({ reference: '', name: '', gender: '', age: '' });
            setSelectedTeeth([]);
            setTeethConfig([]);
            setAppointmentDate('');
            setGamme('nova');
            setNotes('');
            inProgressConfigRef.current = null;
            setEditingLineId(null);
            showToast("Brouillon réinitialisé", "info");
        }
    };

    const saveOrder = async () => {
        if (teethConfig.length === 0) {
            showToast('Attention: Aucune prestation configurée', 'warning');
            return;
        }
        if (!practitionerInfo.name) {
            showToast('Veuillez renseigner le praticien', 'error');
            return;
        }

        const orderData = {
            clientId: practitionerInfo.id || null,
            practitionerName: practitionerInfo.name,
            patientName: patientInfo.name,
            patientReference: patientInfo.reference,
            teethConfig: teethConfig,
            gamme: gamme,
            appointmentDate: appointmentDate,
            notes: notes,
        };

        try {
            if (orderId) {
                await updateOrder(orderId, orderData);
                showToast('Fiche de travail mise à jour !');
                setTimeout(() => navigate('/jobs'), 1500);
            } else {
                const createdOrder = await addOrder({
                    ...orderData,
                    status: 'Reçu',
                    createdAt: new Date().toISOString()
                });

                // Clear saved draft upon successful creation
                sessionStorage.removeItem(DRAFT_STORAGE_KEY);

                const caseNum = (createdOrder?.caseNumber || '').toUpperCase();
                const numMatch = caseNum.match(/\d+/);
                const caseDigits = numMatch ? parseInt(numMatch[0], 10) : null;
                const isEno300 = caseNum === 'ENO-300' || caseNum.endsWith('-300') || caseDigits === 300;
                const isEno100 = caseNum === 'ENO-100' || caseNum.endsWith('-100') || caseDigits === 100;

                if (isEno300) {
                    setCreatedCaseNumber(caseNum || 'ENO-300');
                    setShowEno300Modal(true);
                    triggerRocketCelebration();
                } else if (isEno100) {
                    setCreatedCaseNumber(caseNum || 'ENO-100');
                    setShowEno100Modal(true);
                    triggerCelebrationConfetti();
                } else {
                    showToast('Fiche de travail créée avec succès !');
                    setTimeout(() => navigate('/jobs'), 1500);
                }
            }
        } catch (error) {
            console.error(error);
            showToast("Erreur lors de l'enregistrement. Vérifiez votre connexion ou les droits d'accès.", 'error');
        }
    };

    const handeClientSelect = (e) => {
        const client = clients.find(c => c.id === e.target.value);
        if (client) {
            setPractitionerInfo({
                id: client.id, name: client.name, address: client.address, phone: client.phone, email: client.email
            });
        }
    };

    const handleToothSelect = (toothId) => {
        // When selecting a tooth, clear any arcade selection
        setSelectedTeeth(prev => {
            const withoutArcades = prev.filter(id => id !== 'HAUT' && id !== 'BAS');
            return withoutArcades.includes(toothId)
                ? withoutArcades.filter(id => id !== toothId)
                : [...withoutArcades, toothId];
        });
    };

    const handleArcadeSelect = (arcadeId) => {
        setSelectedTeeth(prev => {
            // Toggle arcade: if already selected, deselect; otherwise select only this arcade
            if (prev.includes(arcadeId)) {
                return prev.filter(id => id !== arcadeId);
            }
            // Clear individual teeth and other arcades, keep only this arcade
            const otherArcades = prev.filter(id => id === 'HAUT' || id === 'BAS');
            return [...otherArcades.filter(id => id !== arcadeId), arcadeId];
        });
    };

    const clearSelection = () => {
        setSelectedTeeth([]);
        setEditingLineId(null);
        inProgressConfigRef.current = null;
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const startEditLine = (line) => {
        setEditingLineId(line.id);
        setSelectedTeeth([...(line.teeth || [])]);
    };

    const cancelEditLine = () => {
        setEditingLineId(null);
        setSelectedTeeth([]);
        inProgressConfigRef.current = null;
    };

    const isArcadeSelection = selectedTeeth.some(t => t === 'HAUT' || t === 'BAS');

    const updateToothConfig = (config) => {
        if (selectedTeeth.length > 0) {
            // Keep arcade IDs as strings, sort numeric teeth
            const sortedTeeth = [...selectedTeeth].sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
                if (typeof a === 'string') return -1;
                if (typeof b === 'string') return 1;
                return Number(a) - Number(b);
            });
            if (editingLineId) {
                setTeethConfig(prev => prev.map(line => {
                    if (line.id === editingLineId) {
                        return {
                            ...line,
                            teeth: sortedTeeth,
                            ...config
                        };
                    }
                    return line;
                }));
                showToast(`Prestation mise à jour avec succès !`);
                setEditingLineId(null);
            } else {
                const newLine = {
                    id: Date.now().toString(),
                    teeth: sortedTeeth,
                    ...config
                };
                setTeethConfig(prev => [...prev, newLine]);
                const label = isArcadeSelection
                    ? `Prestation ajoutée pour ${sortedTeeth.map(t => t === 'HAUT' ? 'Arcade Haut' : t === 'BAS' ? 'Arcade Bas' : `dent ${t}`).join(', ')}`
                    : `Prestation ajoutée pour la sélection`;
                showToast(label);
            }
            setSelectedTeeth([]);
            inProgressConfigRef.current = null;
        }
    };

    const deleteOrderLine = (lineId) => {
        setTeethConfig(prev => prev.filter(line => line.id !== lineId));
        if (lineId === editingLineId) {
            setEditingLineId(null);
            setSelectedTeeth([]);
        }
    };

    const handleApplyImportedData = (data) => {
        if (!data) return;

        // 1. Practitioner matching by token / name against clients database
        if (data.practitionerName) {
            const searchTokens = data.practitionerName
                .toLowerCase()
                .replace(/dr\.?|cabinet|dentaire|beausoleil/g, '')
                .trim()
                .split(/\s+/);

            const matchedClient = clients.find(c => {
                const clientNameLower = c.name.toLowerCase();
                return searchTokens.some(token => token.length > 2 && clientNameLower.includes(token));
            });

            if (matchedClient) {
                setPractitionerInfo({
                    id: matchedClient.id,
                    name: matchedClient.name,
                    address: matchedClient.address || '',
                    phone: matchedClient.phone || '',
                    email: matchedClient.email || ''
                });
            } else {
                setPractitionerInfo(prev => ({
                    ...prev,
                    id: '',
                    name: data.practitionerName
                }));
            }
        }

        // 2. Patient Info & Reference
        setPatientInfo(prev => ({
            ...prev,
            name: data.patientName || prev.name,
            reference: data.orderId || prev.reference
        }));

        // 3. Appointment Date
        if (data.deliveryDate) {
            setAppointmentDate(data.deliveryDate);
        }

        // 4. Technical Notes (Memo scanner)
        if (data.notes) {
            setNotes(data.notes);
        }

        showToast(`Informations pré-remplies : Dentiste, Patient, Référence & Date de livraison !`);
    };

    return (
        <div className="new-job-page">
            <div className="new-job-header d-flex justify-content-between align-items-end flex-wrap gap-3">
                <div>
                    <h2 className="animate__animated animate__fadeInLeft">
                        {orderId ? 'Modifier la Fiche' : 'Nouvelle Fiche de Travail'}
                    </h2>
                    <p className="text-muted mb-0">Renseignez les informations et configurez le travail prothétique.</p>
                </div>

                <div className="header-controls d-flex align-items-center gap-2 flex-wrap">
                    <OrderScreenshotParser onApplyData={handleApplyImportedData} />

                    <div className="control-group ms-md-2">
                        <label>Client</label>
                        <select className="form-select" onChange={handeClientSelect} value={practitionerInfo.id || ''}>
                            <option value="">Charger un profil...</option>
                            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="control-group">
                        <label>Gamme</label>
                        <select className="form-select" value={gamme} onChange={(e) => setGamme(e.target.value)}>
                            <option value="nova">Finition Nova</option>
                        </select>
                    </div>

                    {!orderId && Boolean(
                        practitionerInfo.name || patientInfo.name || notes || teethConfig.length > 0 || selectedTeeth.length > 0
                    ) && (
                        <button 
                            type="button" 
                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                            style={{ height: '38px', borderRadius: '8px', fontWeight: '600', padding: '0 12px' }}
                            onClick={handleResetDraft}
                            title="Effacer le brouillon mémorisé et réinitialiser la fiche"
                        >
                            <FaTrashAlt size={12} /> Réinitialiser
                        </button>
                    )}

                    <button className="btn-save-job" onClick={saveOrder}>
                        <FaSave /> {orderId ? 'Enregistrer' : 'Créer la Fiche'}
                    </button>
                </div>
            </div>

            <div className="job-layout-grid m-0">
                {/* Left Side: Forms */}
                <div className="job-sidebar">
                    <PractitionerForm
                        practitionerInfo={practitionerInfo}
                        updatePractitionerInfo={(info) => setPractitionerInfo(info)}
                        patientInfo={patientInfo}
                        updatePatientInfo={(info) => setPatientInfo(info)}
                        appointmentDate={appointmentDate}
                        handleAppointmentDateChange={setAppointmentDate}
                        notes={notes}
                        handleNotesChange={setNotes}
                    />
                </div>

                {/* Right Side: Visual & Config */}
                <div className="job-main-section">
                    <div className="modern-card dental-arch-card shadow-sm">
                        <div className="modern-card-header">
                            <h3><FaTeeth className="text-primary" /> Sélection des Dents</h3>
                            {selectedTeeth.length > 0 && (
                                <button className="btn btn-sm btn-link text-danger text-decoration-none" onClick={clearSelection}>
                                    <FaUndo className="me-1" /> Réinitialiser
                                </button>
                            )}
                        </div>
                        <div className="modern-card-body p-0">
                            <DentalArch
                                selectedTeeth={selectedTeeth}
                                onToothSelect={handleToothSelect}
                                onArcadeSelect={handleArcadeSelect}
                                teethConfig={teethConfig}
                            />
                        </div>
                        <div className="dental-arch-footer">
                            <span className="selected-count">
                                {isArcadeSelection
                                    ? selectedTeeth.map(t => t === 'HAUT' ? '⬆ Arcade Haut' : t === 'BAS' ? '⬇ Arcade Bas' : `Dent ${t}`).join(', ')
                                    : `${selectedTeeth.length} dent${selectedTeeth.length > 1 ? 's' : ''} sélectionnée${selectedTeeth.length > 1 ? 's' : ''}`
                                }
                            </span>
                            <small className="text-muted">Cliquez sur une dent ou sélectionnez une arcade (Haut/Bas)</small>
                        </div>
                    </div>


                    {/* Configuration & Order List Section */}
                    <div className="bottom-panels-grid">
                        <div className="modern-card job-config-card">
                            <div className="modern-card-header">
                                <h3>
                                    <FaLayerGroup className="text-primary" />{' '}
                                    {selectedTeeth.length > 0
                                        ? (editingLineId ? "Modification de la Prestation" : (isArcadeSelection ? "Configuration Arcade" : "Configuration Technique"))
                                        : "Liste des Prestations"}
                                </h3>
                            </div>
                            <div className="modern-card-body p-0">
                                {selectedTeeth.length > 0 ? (
                                    <ConfigPanel
                                        toothIds={selectedTeeth}
                                        config={editingLineId ? (teethConfig.find(l => l.id === editingLineId) || {}) : (initialInProgressConfig || {})}
                                        isEditing={Boolean(editingLineId)}
                                        onCancel={cancelEditLine}
                                        updateConfig={updateToothConfig}
                                        onDraftChange={handleDraftChange}
                                    />
                                ) : (
                                    <div className="order-list-empty-state p-4 text-center">
                                        {teethConfig.length === 0 ? (
                                            <>
                                                <div className="mb-3 opacity-20"><FaTeeth size={48} /></div>
                                                <p className="text-muted">Sélectionnez des dents sur le schéma pour commencer à ajouter des prestations.</p>
                                            </>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-hover order-list-table m-0 align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>Dents</th>
                                                            <th>Prestation</th>
                                                            <th>Quantité</th>
                                                            <th>Détails</th>
                                                            <th className="text-end">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {teethConfig.map((line, idx) => (
                                                            <tr key={line.id || idx} className={line.id === editingLineId ? 'table-primary' : ''}>
                                                                <td className="fw-bold text-primary">{line.teeth ? line.teeth.map(t => t === 'HAUT' ? '⬆ Haut' : t === 'BAS' ? '⬇ Bas' : t).join(', ') : '-'}</td>
                                                                <td className="small">{line.name}</td>
                                                                <td className="fw-bold">
                                                                    {Number(line.quantity) === 0 ? (
                                                                        <span className="badge bg-warning text-dark border" title="Travail sous garantie (Non facturé)">
                                                                            0 (À refaire / 0 €)
                                                                        </span>
                                                                    ) : (
                                                                        line.quantity !== undefined ? line.quantity : (line.teeth?.length || 1)
                                                                    )}
                                                                </td>
                                                                <td className="small text-muted">
                                                                    {line.details?.teinte && <span className="badge bg-light text-dark border me-1">Teinte: {line.details.teinte}</span>}
                                                                    {line.notes && <div className="text-truncate" style={{ maxWidth: '150px' }}>{line.notes}</div>}
                                                                </td>
                                                                <td className="text-end">
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary border-0 me-1"
                                                                        onClick={() => startEditLine(line)}
                                                                        title="Modifier cette prestation"
                                                                    >
                                                                        <FaEdit />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-danger border-0"
                                                                        onClick={() => deleteOrderLine(line.id)}
                                                                        title="Supprimer"
                                                                    >
                                                                        <FaTrashAlt />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* Modal de célébration historique pour le cas ENO-100 */}
            {showEno100Modal && (
                <div className="eno100-celebration-overlay">
                    <div className="eno100-celebration-card">
                        <div className="eno100-trophy-badge">
                            <FaTrophy className="eno100-trophy-icon" />
                        </div>
                        <div className="eno100-sparkles">✨ 🎉 🎈 🎉 ✨</div>
                        <h1 className="eno100-title">INCROYABLE ! CAP DES 100 !</h1>
                        
                        <div className="eno100-case-pill">
                            <span className="eno100-case-label">DOSSIER HISTORIQUE</span>
                            <strong className="eno100-case-num">{createdCaseNumber || 'ENO-100'}</strong>
                        </div>

                        <p className="eno100-message">
                            🎉 <strong>FÉLICITATIONS !</strong> Vous venez d'enregistrer le <strong>100ème dossier</strong> du laboratoire E-novadent !
                        </p>
                        
                        <p className="eno100-subtitle">
                            C'est une étape symbolique majeure ! Un immense merci et bravo pour votre dévouement et votre travail au quotidien ! 🚀🦷
                        </p>

                        <div className="eno100-actions">
                            <button
                                type="button"
                                className="btn-eno100-confetti"
                                onClick={triggerCelebrationConfetti}
                            >
                                🎊 Encore des confettis !
                            </button>
                            <button
                                type="button"
                                className="btn-eno100-finish"
                                onClick={() => navigate('/jobs')}
                            >
                                <FaRocket className="me-2" /> Continuer vers les dossiers
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Animation comique de la fusée décollant vers le ciel */}
            <RocketCelebration
                active={rocketActive}
                onComplete={() => setRocketActive(false)}
                caseNumber={createdCaseNumber || 'ENO-300'}
            />

            {/* Modal de célébration historique pour le cas ENO-300 */}
            {showEno300Modal && (
                <div className="eno300-celebration-overlay">
                    <div className="eno300-celebration-card">
                        <div className="eno300-space-badge">
                            <FaRocket className="eno300-rocket-icon" />
                        </div>
                        <div className="eno300-sparkles">🪐 🚀 ✨ 🌕 ✨ 🛸 🌟</div>
                        <h1 className="eno300-title">BRAVO POUR LE 300ème CAS !</h1>
                        
                        <div className="eno300-case-pill">
                            <span className="eno300-case-label">CAP HISTORIQUE ATTEINT</span>
                            <strong className="eno300-case-num">{createdCaseNumber || 'ENO-300'}</strong>
                        </div>

                        <p className="eno300-message">
                            🚀 <strong>FÉLICITATIONS !</strong> Vous venez de franchir le cap monumental des <strong>300 dossiers créés</strong> au laboratoire E-novadent !
                        </p>
                        
                        <p className="eno300-subtitle">
                            Vitesse de libération atteinte ! La fusée est en orbite et la prothèse file vers les étoiles ! Félicitations à toute l'équipe pour ce travail quotidien remarquable ! 🌟🦷✨
                        </p>

                        <div className="eno300-actions">
                            <button
                                type="button"
                                className="btn-eno300-launch"
                                onClick={triggerRocketCelebration}
                            >
                                <FaRocket className="me-2" /> 🚀 Relancer la fusée & confettis !
                            </button>
                            <button
                                type="button"
                                className="btn-eno300-finish"
                                onClick={() => navigate('/jobs')}
                            >
                                Continuer vers les dossiers
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NewJob;
